/** Thin fetch wrapper: same-origin cookies, CSRF header, JSON envelope, typed errors. */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public payload: Record<string, unknown>,
  ) {
    super(code);
  }
  get retryAfter(): number | undefined {
    const v = this.payload["retry_after"];
    return typeof v === "number" ? v : undefined;
  }
}

function getCookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="))
    ?.split("=")[1];
}

export async function api<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  const method = (init.method ?? "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") {
    const token = getCookie("csrftoken");
    if (token) headers.set("X-CSRFToken", token);
  }
  let body = init.body;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }
  const response = await fetch(path, {
    ...init,
    method,
    headers,
    body,
    credentials: "same-origin",
  });
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!response.ok) {
    const code =
      typeof data["code"] === "string" ? (data["code"] as string) : `http_${response.status}`;
    throw new ApiError(response.status, code, data);
  }
  return data as T;
}
